import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import {
  User, Camera, Lock, CheckCircle, Warning, FloppyDisk, Eye, EyeSlash
} from '@phosphor-icons/react';

const ROLES = {
  admin: 'System Admin',
  factory_manager: 'Factory Manager',
  quality_manager: 'Quality Manager',
  sales_manager: 'Sales Manager',
  dispatch_user: 'Dispatch User',
};

const DEPARTMENTS = [
  '', 'Administration', 'Factory Floor', 'Quality Control',
  'Sales & Marketing', 'Dispatch & Logistics', 'Procurement', 'Finance',
];

export default function Settings() {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const fileRef = useRef(null);

  // Profile form
  const [profile, setProfile] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    department: user?.department || '',
    bio: user?.bio || '',
  });

  // Avatar
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Password form
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });

  // ARIA AI settings
  const [aiConfirm, setAiConfirm] = useState(user?.ai_confirm !== false);
  const [aiSaving, setAiSaving] = useState(false);

  const handleAiConfirmToggle = async () => {
    const next = !aiConfirm;
    setAiConfirm(next);
    setAiSaving(true);
    try {
      await api.patch('/users/me/settings', { ai_confirm: next });
      if (updateUser) updateUser({ ai_confirm: next });
    } catch {
      setAiConfirm(!next); // revert on error
    }
    setAiSaving(false);
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Save profile
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profile.name.trim()) { showToast('Name is required', 'error'); return; }
    setSaving(true);
    try {
      const { data } = await api.put('/users/me', {
        name: profile.name.trim(),
        phone: profile.phone.trim(),
        department: profile.department,
        bio: profile.bio.trim(),
      });
      updateUser(data);
      showToast('Profile saved successfully');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Avatar file pick
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Please select an image file', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('Image must be under 5MB', 'error'); return; }

    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);

    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/users/me/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateUser({ ...user, avatar_url: data.avatar_url });
      setAvatarPreview(data.avatar_url);
      showToast('Avatar updated');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Avatar upload failed', 'error');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      showToast('New passwords do not match', 'error'); return;
    }
    if (pwForm.new_password.length < 8) {
      showToast('Password must be at least 8 characters', 'error'); return;
    }
    setSaving(true);
    try {
      await api.post('/users/me/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwForm({ current_password: '', new_password: '', confirm: '' });
      showToast('Password changed successfully');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to change password', 'error');
    } finally {
      setSaving(false);
    }
  };

  const initials = (user?.name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const TAB_STYLE = (active) => ({
    padding: '8px 16px',
    background: active ? 'hsl(197,100%,48%)' : 'transparent',
    color: active ? '#000' : 'hsl(0,0%,55%)',
    border: 'none',
    cursor: 'pointer',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    transition: 'all 0.15s',
    borderBottom: active ? '2px solid hsl(197,100%,48%)' : '2px solid transparent',
  });

  const INPUT = {
    width: '100%',
    padding: '10px 12px',
    background: 'hsl(0,0%,8%)',
    border: '1px solid hsl(0,0%,20%)',
    color: 'hsl(0,0%,90%)',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  };

  const LABEL = {
    display: 'block',
    fontSize: 10,
    color: 'hsl(0,0%,50%)',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    marginBottom: 6,
    fontFamily: "'JetBrains Mono', monospace",
    fontWeight: 700,
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      {/* Toast */}
      {toast && (
        <div data-testid="settings-toast" style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', background: toast.type === 'error' ? 'hsl(0,80%,45%)' : 'hsl(142,70%,40%)',
          color: '#fff', fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
          letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        }}>
          {toast.type === 'error' ? <Warning size={14} /> : <CheckCircle size={14} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 24, fontWeight: 700,
          color: 'hsl(0,0%,95%)', margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Account Settings
        </h1>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,40%)',
          margin: '4px 0 0', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          {user?.email} · {ROLES[user?.role] || user?.role}
        </p>
      </div>

      {/* Avatar card */}
      <div style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,14%)',
        padding: 24, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 20 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div style={{
            width: 72, height: 72,
            background: avatarPreview ? 'transparent' : 'hsl(197,100%,48%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, fontWeight: 700, color: '#fff',
            overflow: 'hidden', flexShrink: 0,
          }}>
            {avatarPreview
              ? <img src={avatarPreview} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : initials
            }
          </div>
          <button
            data-testid="avatar-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploadingAvatar}
            style={{
              position: 'absolute', bottom: -4, right: -4,
              width: 24, height: 24, background: 'hsl(197,100%,48%)',
              border: 'none', cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', color: '#000',
            }}>
            {uploadingAvatar
              ? <div style={{ width: 10, height: 10, border: '2px solid #000', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
              : <Camera size={12} weight="bold" />
            }
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={handleAvatarChange} data-testid="avatar-file-input" />
        </div>
        <div>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, fontWeight: 700,
            color: 'hsl(0,0%,95%)', marginBottom: 4 }}>{user?.name}</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
            color: 'hsl(197,100%,48%)', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
            {ROLES[user?.role] || user?.role}
          </div>
          {user?.department && (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              color: 'hsl(0,0%,45%)', marginTop: 2 }}>{user.department}</div>
          )}
          <div style={{ fontSize: 10, color: 'hsl(0,0%,35%)', marginTop: 4,
            fontFamily: "'JetBrains Mono', monospace" }}>
            Click camera icon to update photo
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid hsl(0,0%,14%)', marginBottom: 24, display: 'flex', gap: 0 }}>
        <button style={TAB_STYLE(activeTab === 'profile')} onClick={() => setActiveTab('profile')}
          data-testid="tab-profile">
          <User size={12} style={{ marginRight: 6 }} />Profile
        </button>
        <button style={TAB_STYLE(activeTab === 'security')} onClick={() => setActiveTab('security')}
          data-testid="tab-security">
          <Lock size={12} style={{ marginRight: 6 }} />Security
        </button>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <form onSubmit={handleSaveProfile}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={LABEL}>Full Name *</label>
              <input
                data-testid="profile-name"
                style={INPUT}
                value={profile.name}
                onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                placeholder="Your full name"
                onFocus={e => e.target.style.borderColor = 'hsl(197,100%,48%)'}
                onBlur={e => e.target.style.borderColor = 'hsl(0,0%,20%)'}
              />
            </div>
            <div>
              <label style={LABEL}>Phone</label>
              <input
                data-testid="profile-phone"
                style={INPUT}
                value={profile.phone}
                onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                placeholder="+91 98765 43210"
                onFocus={e => e.target.style.borderColor = 'hsl(197,100%,48%)'}
                onBlur={e => e.target.style.borderColor = 'hsl(0,0%,20%)'}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={LABEL}>Department</label>
            <select
              data-testid="profile-department"
              style={{ ...INPUT }}
              value={profile.department}
              onChange={e => setProfile(p => ({ ...p, department: e.target.value }))}
            >
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d || '— Select department —'}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={LABEL}>Bio</label>
            <textarea
              data-testid="profile-bio"
              style={{ ...INPUT, minHeight: 80, resize: 'vertical' }}
              value={profile.bio}
              onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
              placeholder="A short note about your role..."
              onFocus={e => e.target.style.borderColor = 'hsl(197,100%,48%)'}
              onBlur={e => e.target.style.borderColor = 'hsl(0,0%,20%)'}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={LABEL}>Email Address</label>
            <input
              style={{ ...INPUT, color: 'hsl(0,0%,40%)', cursor: 'not-allowed' }}
              value={user?.email || ''}
              disabled
            />
            <div style={{ fontSize: 10, color: 'hsl(0,0%,35%)', marginTop: 4,
              fontFamily: "'JetBrains Mono', monospace" }}>
              Email cannot be changed
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={LABEL}>Role</label>
            <input
              style={{ ...INPUT, color: 'hsl(0,0%,40%)', cursor: 'not-allowed' }}
              value={ROLES[user?.role] || user?.role || ''}
              disabled
            />
            <div style={{ fontSize: 10, color: 'hsl(0,0%,35%)', marginTop: 4,
              fontFamily: "'JetBrains Mono', monospace" }}>
              Contact admin to change role
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="save-profile-btn"
            style={{
              padding: '10px 24px',
              background: saving ? 'hsl(0,0%,20%)' : 'hsl(197,100%,48%)',
              border: 'none', color: '#000',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              transition: 'background 0.15s',
            }}>
            <FloppyDisk size={14} />
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <form onSubmit={handleChangePassword}>
          <div style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,14%)',
            padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <Warning size={14} color="hsl(43,96%,56%)" style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,55%)', lineHeight: 1.5 }}>
              Password must be at least 8 characters. Choose a strong, unique password.
            </div>
          </div>

          {[
            { key: 'current_password', label: 'Current Password', testid: 'pw-current' },
            { key: 'new_password', label: 'New Password', testid: 'pw-new' },
            { key: 'confirm', label: 'Confirm New Password', testid: 'pw-confirm' },
          ].map(({ key, label, testid }) => (
            <div key={key} style={{ marginBottom: 16, position: 'relative' }}>
              <label style={LABEL}>{label}</label>
              <div style={{ position: 'relative' }}>
                <input
                  data-testid={testid}
                  type={showPw[key.replace('current_password','current').replace('new_password','new')] ? 'text' : 'password'}
                  style={{ ...INPUT, paddingRight: 40 }}
                  value={pwForm[key]}
                  onChange={e => setPwForm(p => ({ ...p, [key]: e.target.value }))}
                  placeholder={key === 'current_password' ? 'Enter current password' : 'Enter new password'}
                  onFocus={e => e.target.style.borderColor = 'hsl(197,100%,48%)'}
                  onBlur={e => e.target.style.borderColor = 'hsl(0,0%,20%)'}
                />
                <button type="button"
                  onClick={() => setShowPw(p => {
                    const k = key.replace('current_password','current').replace('new_password','new');
                    return { ...p, [k]: !p[k] };
                  })}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'hsl(0,0%,45%)', display: 'flex', alignItems: 'center' }}>
                  {showPw[key.replace('current_password','current').replace('new_password','new')]
                    ? <EyeSlash size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
          ))}

          <button
            type="submit"
            disabled={saving}
            data-testid="change-password-btn"
            style={{
              padding: '10px 24px',
              background: saving ? 'hsl(0,0%,20%)' : 'hsl(197,100%,48%)',
              border: 'none', color: '#000',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
            <Lock size={14} />
            {saving ? 'Updating...' : 'Change Password'}
          </button>
        </form>
      )}

      {/* ARIA AI */}
      <div style={{ borderTop: '1px solid hsl(0,0%,12%)', paddingTop: 20, marginTop: 20 }}>
        <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 13,
          color: 'hsl(0,0%,90%)', textTransform: 'uppercase', letterSpacing: '.12em', marginBottom: 16 }}>
          ARIA AI
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px', background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,14%)' }}>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,80%)', marginBottom: 4 }}>
              Action Confirmation
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)' }}>
              {aiConfirm
                ? 'ARIA will ask before taking any action'
                : 'ARIA acts immediately — autonomous mode'}
            </div>
          </div>
          <button
            data-testid="ai-confirm-toggle"
            onClick={handleAiConfirmToggle}
            disabled={aiSaving}
            style={{
              width: 44, height: 24, cursor: aiSaving ? 'not-allowed' : 'pointer',
              background: aiConfirm ? 'hsl(75,100%,35%)' : 'hsl(0,0%,18%)',
              border: 'none', position: 'relative', transition: 'background 0.2s',
            }}
          >
            <div style={{
              position: 'absolute', top: 4,
              left: aiConfirm ? 24 : 4,
              width: 16, height: 16,
              background: aiConfirm ? 'hsl(75,100%,60%)' : 'hsl(0,0%,50%)',
              transition: 'left 0.2s',
            }} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        select option { background: hsl(0,0%,8%); color: hsl(0,0%,90%); }
      `}</style>
    </div>
  );
}
